import { api } from "@/lib/api";

const num = (v, dp = 0) => (Number(v) || 0).toLocaleString(undefined, { maximumFractionDigits: dp });
const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");

function matchEntry(dirs, text) {
  const t = norm(text);
  if (!t) return null;
  let streetHit = null;
  for (const e of dirs) {
    const name = norm(e.company_name);
    if (name && name.length > 3 && t.includes(name)) return e;
    for (const a of e.addresses || []) {
      const street = norm((a.text || "").split("\n")[0]);
      if (street && street.length > 5 && t.includes(street)) streetHit = streetHit || e;
    }
  }
  return streetHit;
}

function matchAddress(entry, text) {
  const addrs = entry?.addresses || [];
  const t = norm(text);
  for (const a of addrs) {
    const street = norm((a.text || "").split("\n")[0]);
    if (street && street.length > 5 && t.includes(street)) return a;
  }
  return addrs.length === 1 ? addrs[0] : null;
}

function pocBlock(label, entry, addrId) {
  const pocs = (entry?.contacts || []).filter((c) => {
    const ids = c.poc_address_ids || [];
    if (!c.shipment_poc && !ids.length) return false;
    if (!addrId || !ids.length) return true; // POC for all locations, or no specific address matched
    return ids.includes(addrId);
  });
  if (!pocs.length) return [];
  const out = ["", `${label}:`];
  pocs.forEach((c, i) => {
    if (i > 0) out.push("");
    out.push([c.name, c.title].filter(Boolean).join(" — "));
    if (c.phone_mobile) out.push(`Mobile: ${c.phone_mobile}`);
    if (c.phone) out.push(`Office: ${c.phone}`);
    if (c.email) out.push(`Email: ${c.email}`);
  });
  return out;
}

function locationBlock(label, name, addrText, hoo, instructions) {
  const out = ["", `${label}:`];
  if (name) out.push(name);
  const nn = norm(name);
  out.push(...String(addrText || "").split("\n").map((x) => x.trim()).filter(Boolean)
    .filter((x) => { const nx = norm(x); return !(nn && (nx === nn || nx.startsWith(nn) || nn.startsWith(nx))); }));
  if (hoo) out.push(`HOO: ${hoo}`);
  if (instructions) out.push(`Delivery Instructions: ${String(instructions).replace(/\s*\n\s*/g, " ")}`);
  return out;
}

export async function shopCarriersMailto(d, company, landFreight, customers = [], products = [], soShipTo = "") {
  const emails = [...new Set((landFreight || []).flatMap((f) => (f.contacts || []).filter((c) => !c.suppress_email).map((c) => (c.email || "").trim()).filter(Boolean)))];
  const lines = (d.lines || []).map((l) => {
    const cfg = (products.find((p) => p.id === l.product_id)?.pack_configs || []).find((c) => c.id === l.config_id);
    const dims = [cfg?.pallet_length, cfg?.pallet_width, cfg?.pallet_height];
    const dimStr = dims.some((x) => Number(x) > 0) ? dims.map((x) => (Number(x) > 0 ? Number(x).toLocaleString() : "?")).join("x") : "";
    const wpp = l.weight_per_pallet || Number(cfg?.weight_per_pallet) || null;
    const cpp = l.cases_per_pallet || Number(cfg?.cases_per_pallet) || null;
    const pallets = l.pallets != null ? l.pallets : cpp ? Math.round((l.cases / cpp) * 100) / 100 : null;
    const wpc = l.weight_per_case || Number(cfg?.weight_per_case) || null;
    const weight = wpp && pallets != null ? Math.round(pallets * wpp * 10) / 10
      : wpc ? Math.round(l.cases * wpc * 10) / 10 : l.weight_lbs ?? null;
    return { ...l, pallet_dims: l.pallet_dims || dimStr, weight_per_pallet: wpp, pallets, weight_lbs: weight };
  });
  const totalPallets = Math.round(lines.reduce((s, l) => s + (l.pallets || 0), 0) * 100) / 100;
  const weight = d.total_weight_lbs != null ? d.total_weight_lbs
    : Math.round(lines.reduce((s, l) => s + (l.weight_lbs || 0), 0) * 10) / 10;

  // pull every directory so origin/destination can match any contact type
  let dirs = [];
  try {
    const res = await Promise.all(["/suppliers", "/co-packers", "/customers", "/distributors"].map((u) => api.get(u).catch(() => ({ data: [] }))));
    dirs = res.flatMap((r) => r.data || []);
  } catch { dirs = []; }

  // destination
  const cust = customers.find((x) => x.id === d.customer_id) || null;
  const addrs = cust?.addresses || [];
  const hit = addrs.find((a) => a.id === d.ship_address_id) || (addrs.length === 1 ? addrs[0] : null);
  const shipToAddr = d.ship_to || hit?.text || soShipTo || cust?.address || "";
  const destEntry = (cust && dirs.find((e) => e.id === cust.id)) || cust || matchEntry(dirs, `${d.customer_name || ""} ${shipToAddr}`);
  const destAddr = hit || matchAddress(destEntry, shipToAddr);
  const destName = destEntry?.company_name || d.customer_name || "";

  // origin
  const shipFrom = d.ship_from || [company.company_name, company.address_line1, company.address_line2].filter(Boolean).join("\n");
  const origEntry = matchEntry(dirs, shipFrom);
  const origAddr = matchAddress(origEntry, shipFrom);
  const origName = origEntry?.company_name || String(shipFrom).split("\n")[0] || "";
  const origAddrText = String(shipFrom).split("\n").slice(norm(String(shipFrom).split("\n")[0]) === norm(origName) ? 1 : 0).join("\n") || origAddr?.text || "";

  // shipment block — uniform dims/weight gets the compact format, otherwise per-line
  const dimSet = [...new Set(lines.map((l) => l.pallet_dims).filter(Boolean))];
  const wppSet = [...new Set(lines.map((l) => l.weight_per_pallet).filter(Boolean))];
  const shipment = ["Shipment:", `${num(totalPallets, 2)} Pallet${totalPallets === 1 ? "" : "s"}`];
  if (dimSet.length <= 1 && wppSet.length <= 1) {
    if (wppSet[0]) shipment.push(`Weight: ${num(wppSet[0], 1)} LB / PLT`);
    if (dimSet[0]) shipment.push(`Dims: ${dimSet[0]}`);
  } else {
    lines.forEach((l) => {
      const bits = [`${num(l.pallets, 2)} Pallet${l.pallets === 1 ? "" : "s"}`];
      if (l.weight_per_pallet) bits.push(`${num(l.weight_per_pallet, 1)} LB / PLT`);
      if (l.pallet_dims) bits.push(`Dims: ${l.pallet_dims}`);
      shipment.push(bits.join(" — "));
    });
  }
  shipment.push(`Total: ${weight ? `${num(weight, 1)} LBS` : "TBD"}`);

  const body = [
    "Dear Freight Provider,",
    "",
    "Please provide a quote for the following shipment:",
    "",
    ...shipment,
    ...locationBlock("Origin", origName, origAddrText, origAddr?.hoo, origAddr?.delivery_instructions),
    ...locationBlock("Destination", destName, destAddr?.text || shipToAddr, destAddr?.hoo, destAddr?.delivery_instructions),
    ...pocBlock("Origin POC", origEntry, origAddr?.id),
    ...pocBlock("Destination POC", destEntry, destAddr?.id),
    "",
    "Thank you,",
    company.company_name || "",
  ].join("\n");
  const subject = `Freight Quote Request — ${d.do_number} (${num(totalPallets, 2)} Pallets${weight ? `, ${num(weight, 1)} lbs` : ""})`;
  return { emails, href: `mailto:operations@infinitybrands.com?bcc=${encodeURIComponent(emails.join(";"))}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}` };
}
